/**
 * Call screen (V3b — SM-9 client UI, route /call) — v3 "AFTER-DARK ARCADE".
 *
 * Sits between the registers: cinematic, low-chrome, dark stage, bloom only
 * on the live controls. Incoming ring = concentric pulse + two clear verbs;
 * in-call = the media IS the screen, controls float on wet glass; end states
 * stay matte (a safety teardown renders only ©call.ended_generic —
 * indistinguishable from a mundane end).
 *
 * One screen, every phase: incoming ring (accept/decline), outgoing ring
 * (cancel), in-call view (remote video/audio + local PiP, mute/camera
 * toggles, hangup), honest degraded-mode note when no TURN relay is
 * configured (©call.turn_unavailable). Presentation + motion only — the WS
 * command plane and the RTC session are byte-identical to v2.
 *
 * Entry points:
 *   - /call?match=<matchId>&kind=voice|video → sends the INVITE on mount
 *     (the conversation screen (V3a) links here from its call buttons);
 *   - a `call.incoming` push (wired app-wide in call-store.ts) → the user
 *     opens /call to answer;
 *   - reload mid-call → GET /calls/active recovery.
 *
 * The server is the referee: every button routes through the WS command
 * twins; pushes update the store; this component renders the store.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatCopy, resolveCopy } from "@humble/shared";
import { motion } from "framer-motion";
import { Button, Card, Mascot, springs } from "../../design/index.js";
import { api } from "../../lib/api.js";
import { useStore } from "../../lib/store.js";
import { ws, wsCommand, WsCommandError } from "../../lib/ws.js";
import {
  IconMic,
  IconMicOff,
  IconPhone,
  IconPhoneDown,
  IconVideo,
  IconVideoOff,
} from "../chat/icons.js";
import { callStore, initCallWiring, recoverActiveCall, resetCall, type CallKind } from "./call-store.js";
import { RtcSession } from "./webrtc.js";
import "./call.css";

initCallWiring();

function endedLine(reason: string | null): string {
  switch (reason) {
    case "missed":
      return formatCopy(resolveCopy("call.missed") ?? "Missed {kind} call", { kind: "" }).trim();
    case "declined":
      return "Declined.";
    case "canceled":
      return "Canceled.";
    case "conn_fail":
      return resolveCopy("call.conn_fail") ?? "Couldn't connect the call.";
    default:
      // hangup / disconnect / safety / anything else: the generic line —
      // a block/unmatch/ban teardown is never distinguishable (D-003/SM-9).
      return resolveCopy("call.ended_generic") ?? "Call ended.";
  }
}

/** Concentric ring pulse behind a face/name — the cinematic ring signal. */
function RingPulse({ tone }: { tone: "in" | "out" | "live" }) {
  return (
    <span className={`call__pulse call__pulse--${tone}`} aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  );
}

export function CallScreen() {
  const state = useStore(callStore, (s) => s);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [turnAvailable, setTurnAvailable] = useState<boolean | null>(null);
  const [busyMsg, setBusyMsg] = useState<string | null>(null);
  const sessionRef = useRef<RtcSession | null>(null);
  const localRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const invitedRef = useRef(false);

  // Recovery + deep-link invite (once per mount).
  useEffect(() => {
    ws.ensureConnected();
    const s = callStore.get();
    if (s.phase === "idle") void recoverActiveCall();
    const match = params.get("match");
    const kind = (params.get("kind") === "video" ? "video" : "voice") as CallKind;
    if (match && !invitedRef.current && callStore.get().phase === "idle") {
      invitedRef.current = true;
      void (async () => {
        try {
          const res = await wsCommand<{ call_id: string; state: string; collapsed?: boolean }>(
            "call.invite",
            { match_id: match, kind },
          );
          callStore.set({
            callId: res.call_id,
            matchId: match,
            kind,
            role: "caller",
            phase: res.state === "connecting" ? "connecting" : "outgoing",
            endReason: null,
            error: null,
          });
        } catch (err) {
          const msg = err instanceof WsCommandError ? err.message : "Could not start the call.";
          callStore.set({ phase: "idle", error: msg });
        }
      })();
    }
  }, []);

  // RTC session lives across connecting+active; dies on ended/idle.
  useEffect(() => {
    const live = state.phase === "connecting" || state.phase === "active";
    if (live && !sessionRef.current && state.callId && state.role) {
      const session = new RtcSession(state.callId, state.kind, state.role, {
        onLocalStream(stream) {
          if (localRef.current) localRef.current.srcObject = stream;
        },
        onRemoteStream(stream) {
          if (remoteVideoRef.current) remoteVideoRef.current.srcObject = stream;
          if (remoteAudioRef.current) remoteAudioRef.current.srcObject = stream;
        },
        onFailure(kind) {
          if (kind === "permissions") {
            setBusyMsg(
              formatCopy(resolveCopy("call.perm_denied") ?? "Permission denied: {mic_camera}", {
                mic_camera: callStore.get().kind === "video" ? "camera and microphone" : "microphone",
              }),
            );
            void hangup();
          } else {
            setBusyMsg(resolveCopy("call.conn_fail") ?? "Connection failed.");
          }
        },
      });
      sessionRef.current = session;
      void session.start().then(({ turnAvailable: turn }) => setTurnAvailable(turn));
    }
    if (!live && sessionRef.current) {
      sessionRef.current.close();
      sessionRef.current = null;
    }
    return undefined;
  }, [state.phase, state.callId]);

  // Unmount: media off (the call itself keeps running server-side; /call re-attaches).
  useEffect(
    () => () => {
      sessionRef.current?.close();
      sessionRef.current = null;
    },
    [],
  );

  // Presentation: the caller's push carries no display name — read it from the
  // match view (same read the conversation screen makes) so the stage shows a
  // person, not a placeholder.
  useEffect(() => {
    const { matchId, peerName } = callStore.get();
    if (!matchId || peerName || state.phase === "idle" || state.phase === "ended") return;
    let cancelled = false;
    void api
      .get("/matches/{matchId}", { params: { matchId } })
      .then((view) => {
        const name = (view as unknown as { counterpart?: { display_name?: string | null } })
          .counterpart?.display_name;
        if (!cancelled && name && !callStore.get().peerName) callStore.set({ peerName: name });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [state.matchId, state.phase]);

  // Presentation: a transient ICE wobble can report conn_fail and then the
  // call connects anyway — a live call must not wear a stale scary banner.
  useEffect(() => {
    if (state.phase === "active") setBusyMsg(null);
  }, [state.phase]);

  const command = useCallback(async (t: string) => {
    const id = callStore.get().callId;
    if (!id) return;
    try {
      await wsCommand(t, { call_id: id });
    } catch (err) {
      if (err instanceof WsCommandError && err.code !== "TOO_LATE") setBusyMsg(err.message);
    }
  }, []);

  const accept = useCallback(async () => {
    await command("call.accept");
    callStore.set({ phase: "connecting" });
  }, [command]);
  const decline = useCallback(async () => {
    await command("call.decline");
    callStore.set({ phase: "ended", endReason: "declined" });
  }, [command]);
  const cancel = useCallback(async () => {
    await command("call.cancel");
    callStore.set({ phase: "ended", endReason: "canceled" });
  }, [command]);
  const hangup = useCallback(async () => {
    await command("call.hangup");
    callStore.set({ phase: "ended", endReason: null });
  }, [command]);

  function toggleMute() {
    const next = !muted;
    setMuted(next);
    sessionRef.current?.setMuted(next);
  }
  function toggleCamera() {
    const next = !cameraOff;
    setCameraOff(next);
    sessionRef.current?.setCameraOff(next);
  }

  const kindLabel = state.kind === "video" ? "video" : "voice";
  const peerInitial = (state.peerName ?? "?").slice(0, 1).toUpperCase();

  return (
    <div className="call">
      {state.phase === "idle" && (
        <Card padding="lg" className="call__panel">
          <h1 className="call__idle-title type-display-3">{resolveCopy("call.idle_title") ?? "Calls"}</h1>
          <p className="text-dim">{resolveCopy("call.idle_explainer")}</p>
          {state.error && <p className="call__error">{state.error}</p>}
        </Card>
      )}

      {state.phase === "incoming" && (
        <motion.div
          className="call__panel call__ring"
          role="alertdialog"
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={springs.dramatic}
        >
          <p className="call__kicker h-caps">{resolveCopy("call.incoming_kicker") ?? "incoming"}</p>
          <div className="call__face">
            <RingPulse tone="in" />
            <span className="call__face-disc call__face-disc--ringing">{peerInitial}</span>
          </div>
          <p className="call__ring-title">
            {formatCopy(resolveCopy("call.incoming") ?? "Incoming {kind} call — {name}", {
              kind: kindLabel,
              name: state.peerName ?? "your match",
            })}
          </p>
          <div className="call__ring-actions">
            <Button variant="primary" className="call__answer" onClick={() => void accept()}>
              <IconPhone width={17} height={17} /> {resolveCopy("call.accept") ?? "Accept"}
            </Button>
            <Button variant="danger" onClick={() => void decline()}>
              <IconPhoneDown width={17} height={17} /> {resolveCopy("call.decline") ?? "Decline"}
            </Button>
          </div>
        </motion.div>
      )}

      {state.phase === "outgoing" && (
        <motion.div
          className="call__panel call__ring"
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={springs.dramatic}
        >
          <p className="call__kicker h-caps">{resolveCopy("call.outgoing_kicker") ?? "calling"}</p>
          <div className="call__face">
            <RingPulse tone="out" />
            <span className="call__face-disc">{peerInitial}</span>
          </div>
          <p className="call__ring-title">
            {formatCopy(resolveCopy("call.ringing") ?? "Calling {name}…", {
              name: state.peerName ?? "your match",
            })}
          </p>
          <div className="call__ring-actions">
            <Button variant="ghost" onClick={() => void cancel()}>
              {resolveCopy("call.cancel_label") ?? "Cancel"}
            </Button>
          </div>
        </motion.div>
      )}

      {(state.phase === "connecting" || state.phase === "active") && (
        <div className="call__stage">
          <div className={`call__media ${state.kind === "video" ? "call__media--video" : "call__media--voice"}`}>
            {state.kind === "video" ? (
              <>
                <video ref={remoteVideoRef} className="call__remote" autoPlay playsInline />
                <video ref={localRef} className="call__local" autoPlay playsInline muted />
              </>
            ) : (
              <div className="call__voice-face">
                <div className="call__face">
                  <RingPulse tone="live" />
                  <span className="call__face-disc call__face-disc--live">{peerInitial}</span>
                </div>
                <span className="call__voice-name">{state.peerName ?? "Voice call"}</span>
                <audio ref={remoteAudioRef} autoPlay />
                {/* local monitor element kept for stream attachment symmetry */}
                <video ref={localRef} className="call__hidden" autoPlay playsInline muted />
              </div>
            )}
            <div className="call__status" role="status">
              {state.phase === "connecting" ? (
                <span className="call__status-connecting">
                  {resolveCopy("call.connecting") ?? "Connecting…"}
                </span>
              ) : (
                formatCopy(resolveCopy("call.status_live") ?? "In {kind} call", { kind: kindLabel })
              )}
            </div>
          </div>
          {turnAvailable === false && (
            <p className="call__turn-note">{resolveCopy("call.turn_unavailable")}</p>
          )}
          {/* a failure sting can race the connect and land while live — an
              active call never wears it (the status ribbon is the truth;
              a real teardown ends the call server-side and renders ended) */}
          {busyMsg && state.phase !== "active" && <p className="call__error">{busyMsg}</p>}
          <div className="call__controls glass">
            <button
              type="button"
              className={`call__ctl ${muted ? "call__ctl--engaged" : ""}`}
              onClick={toggleMute}
              aria-pressed={muted}
              aria-label={muted ? (resolveCopy("call.hud_unmute") ?? "Unmute") : (resolveCopy("call.hud_mute") ?? "Mute")}
              title={muted ? (resolveCopy("call.hud_unmute") ?? "Unmute") : (resolveCopy("call.hud_mute") ?? "Mute")}
            >
              {muted ? <IconMicOff width={22} height={22} /> : <IconMic width={22} height={22} />}
            </button>
            {state.kind === "video" && (
              <button
                type="button"
                className={`call__ctl ${cameraOff ? "call__ctl--engaged" : ""}`}
                onClick={toggleCamera}
                aria-pressed={cameraOff}
                aria-label={
                  cameraOff
                    ? (resolveCopy("call.hud_camera_on") ?? "Camera on")
                    : (resolveCopy("call.hud_camera_off") ?? "Camera off")
                }
                title={
                  cameraOff
                    ? (resolveCopy("call.hud_camera_on") ?? "Camera on")
                    : (resolveCopy("call.hud_camera_off") ?? "Camera off")
                }
              >
                {cameraOff ? <IconVideoOff width={22} height={22} /> : <IconVideo width={22} height={22} />}
              </button>
            )}
            <button
              type="button"
              className="call__ctl call__ctl--hangup"
              onClick={() => void hangup()}
              aria-label={resolveCopy("call.hud_hangup") ?? "Hang up"}
              title={resolveCopy("call.hud_hangup") ?? "Hang up"}
            >
              <IconPhoneDown width={24} height={24} />
            </button>
          </div>
        </div>
      )}

      {state.phase === "ended" && (
        <Card padding="lg" className="call__panel call__panel--ended">
          <p className="call__kicker h-caps">{resolveCopy("call.ended_kicker") ?? "call over"}</p>
          <Mascot mood="ko" size={32} />
          <p className="call__ended">{endedLine(state.endReason)}</p>
          <div className="call__ring-actions">
            <Button
              variant="primary"
              onClick={() => {
                resetCall();
                navigate("/matches");
              }}
            >
              {resolveCopy("call.back_to_matches") ?? "Back to matches"}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
